import { AccessInheritanceDecision } from '@/drive/types/AccessInheritanceDecision';

export interface AccessInheritanceDestination {
  typeLabel: string;
}

export interface AccessInheritanceGroup {
  count: number;
  accessTitles: string[];
}

export interface AccessInheritanceSummary {
  publicCount: number;
  own: AccessInheritanceGroup;
  foreign: AccessInheritanceGroup;
  hasOwnContainers: boolean;
}

export interface AccessInheritanceContent {
  title: string;
  paragraphs: string[];
  okLabel: string;
  okDecision: AccessInheritanceDecision;
  keepLabel: Nullable<string>;
  cancelLabel: string;
}

const CANCEL_LABEL = 'Cancel';
const KEEP_AS_IS_LABEL = 'Keep as is';
const MAKE_PUBLIC_LABEL = 'Yes, make public';
const OK_LABEL = 'Ok';

const FALLBACK_ACCESS_LABEL = 'private';

export function getAccessInheritanceContent(
  destination: AccessInheritanceDestination,
  summary: AccessInheritanceSummary,
): AccessInheritanceContent {
  if (summary.own.count === 0) {
    return getUnchangeableContent(destination, summary);
  }

  return getPublishableContent(destination, summary);
}

function getUnchangeableContent(
  destination: AccessInheritanceDestination,
  summary: AccessInheritanceSummary,
): AccessInheritanceContent {
  return {
    title: getUnchangeableTitle(summary),
    paragraphs: [getUnchangeableDescription(destination, summary)],
    okLabel: OK_LABEL,
    okDecision: AccessInheritanceDecision.LeaveUnchanged,
    keepLabel: null,
    cancelLabel: CANCEL_LABEL,
  };
}

function getUnchangeableTitle(summary: AccessInheritanceSummary): string {
  const accessLabel = getAccessLabel(summary.foreign);

  if (summary.foreign.accessTitles.length > 1) {
    return 'Keep these items as they are?';
  }

  if (summary.foreign.count === 1 && summary.publicCount === 0) {
    return `Keep the item ${accessLabel}?`;
  }

  return `Keep these items ${accessLabel}?`;
}

function getUnchangeableDescription(
  destination: AccessInheritanceDestination,
  summary: AccessInheritanceSummary,
): string {
  const { typeLabel } = destination;
  const accessLabel = getAccessLabel(summary.foreign);

  if (summary.publicCount > 0) {
    return `You're adding items to a public ${typeLabel}, including ${accessLabel} items you don't have permission to make public. They will remain ${accessLabel}.`;
  }

  if (summary.foreign.count === 1) {
    return `You're adding ${getArticle(accessLabel)} ${accessLabel} item to a public ${typeLabel}. You don't have permission to make it public, so it will remain ${accessLabel}.`;
  }

  return `You're adding ${accessLabel} items to a public ${typeLabel}. You don't have permission to make them public, so they will remain ${accessLabel}.`;
}

function getPublishableContent(
  destination: AccessInheritanceDestination,
  summary: AccessInheritanceSummary,
): AccessInheritanceContent {
  const paragraphs = [getPublishableDescription(destination, summary)];

  if (summary.hasOwnContainers) {
    paragraphs.push(getNestedContentWarning(summary));
  }

  return {
    title: getPublishableTitle(summary),
    paragraphs,
    okLabel: MAKE_PUBLIC_LABEL,
    okDecision: AccessInheritanceDecision.Inherit,
    keepLabel: getKeepLabel(summary),
    cancelLabel: CANCEL_LABEL,
  };
}

function getPublishableTitle(summary: AccessInheritanceSummary): string {
  if (summary.publicCount === 0 && summary.foreign.count === 0) {
    return summary.own.count === 1 ? 'Make the item public?' : 'Make these items public?';
  }

  const accessLabel = getAccessLabel(summary.own);

  if (summary.own.count === 1) {
    return `Make your ${accessLabel} item public?`;
  }

  return `Make your ${accessLabel} items public?`;
}

function getPublishableDescription(
  destination: AccessInheritanceDestination,
  summary: AccessInheritanceSummary,
): string {
  const { typeLabel } = destination;
  const { publicCount, own, foreign } = summary;
  const hiddenAccessLabel = getHiddenAccessLabel(summary);
  const ownAccessLabel = getAccessLabel(own);

  if (publicCount > 0 && foreign.count > 0) {
    return `You're adding items to a public ${typeLabel}, including ${hiddenAccessLabel} items. ${getCountLabel(own.count)} you own can be made public. The rest belong to other owners and will remain ${getAccessLabel(foreign)}.`;
  }

  if (publicCount > 0) {
    return `You're adding items to a public ${typeLabel}, including ${getLabeledCount(own.count, ownAccessLabel)} you own. ${getPronoun(own.count)} can be made public. Items that are already public won't change.`;
  }

  if (foreign.count > 0) {
    return `You're adding ${hiddenAccessLabel} items to a public ${typeLabel}. ${getCountLabel(own.count)} you own can be made public. The rest belong to other owners and will remain ${getAccessLabel(foreign)}.`;
  }

  if (own.count === 1) {
    return `You're adding ${getArticle(ownAccessLabel)} ${ownAccessLabel} item to a public ${typeLabel}.`;
  }

  return `You're adding ${ownAccessLabel} items to a public ${typeLabel}.`;
}

function getKeepLabel(summary: AccessInheritanceSummary): string {
  const singleAccessTitle = summary.own.count === 1 ? summary.own.accessTitles[0] : null;

  if (singleAccessTitle) {
    return `Keep ${singleAccessTitle}`;
  }

  return KEEP_AS_IS_LABEL;
}

function getNestedContentWarning(summary: AccessInheritanceSummary): string {
  return summary.own.count === 1
    ? 'Everything inside it will become public too.'
    : 'Everything inside them will become public too.';
}

function getHiddenAccessLabel(summary: AccessInheritanceSummary): string {
  return toAccessLabel([...new Set([...summary.own.accessTitles, ...summary.foreign.accessTitles])]);
}

function getAccessLabel(group: AccessInheritanceGroup): string {
  return toAccessLabel(group.accessTitles);
}

function toAccessLabel(accessTitles: string[]): string {
  const lastAccessTitle = accessTitles[accessTitles.length - 1];

  if (!lastAccessTitle) {
    return FALLBACK_ACCESS_LABEL;
  }

  if (accessTitles.length === 1) {
    return lastAccessTitle;
  }

  return `${accessTitles.slice(0, -1).join(', ')} and ${lastAccessTitle}`;
}

function getArticle(accessLabel: string): string {
  return /^[aeiou]/.test(accessLabel) ? 'an' : 'a';
}

function getCountLabel(count: number): string {
  return count === 1 ? '1 item' : `${count} items`;
}

function getLabeledCount(count: number, accessLabel: string): string {
  return count === 1 ? `1 ${accessLabel} item` : `${count} ${accessLabel} items`;
}

function getPronoun(count: number): string {
  return count === 1 ? 'It' : 'They';
}
